/* SMOOZELY ATELIER — accessible, dependency-free interactions. */
(() => {
  'use strict';
  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const money = (cents, currency = 'USD') => {
    try { return new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency }).format(Number(cents) / 100); }
    catch (_err) { return '$' + (Number(cents) / 100).toFixed(2); }
  };

  function initReveals(root = document) {
    const elements = [...root.querySelectorAll('.smo-reveal:not([data-smo-observed])')];
    elements.forEach(el => { el.dataset.smoObserved = '1'; });
    if (!elements.length) return;
    if (!('IntersectionObserver' in window) || prefersReduced || window.Shopify?.designMode) {
      elements.forEach(el => el.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.08, rootMargin: '0px 0px 35px 0px' });
    elements.forEach(el => observer.observe(el));
  }

  function initPalettes(root = document) {
    root.querySelectorAll('[data-smo-palette]:not([data-smo-init])').forEach(group => {
      group.dataset.smoInit = '1';
      const section = group.closest('.smo-palette');
      const stamp = section?.querySelector('[data-smo-selected-color]');
      const swatchDot = section?.querySelector('[data-smo-selected-dot]');
      group.addEventListener('click', event => {
        const button = event.target.closest('.smo-palette-swatch--available');
        if (!button || !group.contains(button)) return;
        group.querySelectorAll('.smo-palette-swatch--available').forEach(el => {
          const chosen = el === button;
          el.classList.toggle('is-selected', chosen);
          el.setAttribute('aria-pressed', String(chosen));
        });
        if (stamp) stamp.textContent = button.dataset.colorName || '';
        if (swatchDot && button.dataset.smoColorHex) {
          swatchDot.style.setProperty('--swatch', button.dataset.smoColorHex);
        }
        const backdrop = section?.querySelector('[data-smo-palette-backdrop]');
        if (backdrop && button.dataset.smoBackdrop) {
          backdrop.src = button.dataset.smoBackdrop;
          backdrop.alt = 'Cloud-Silk sleep mask styled in ' + (button.dataset.colorName || 'selected shade');
        }
        if (button.dataset.smoUrl) section?.querySelectorAll('[data-smo-palette-link]').forEach(link => {
          link.href = button.dataset.smoUrl;
        });
      });
    });
  }

  function initCards(root = document) {
    root.querySelectorAll('[data-smo-card]:not([data-smo-init])').forEach(card => {
      card.dataset.smoInit = '1';
      card.addEventListener('click', event => {
        const swatch = event.target.closest('[data-smo-card-variant]');
        if (!swatch || !card.contains(swatch)) return;
        card.querySelectorAll('[data-smo-card-variant]').forEach(button => {
          const chosen = button === swatch;
          button.classList.toggle('is-selected', chosen);
          button.setAttribute('aria-pressed', String(chosen));
        });
        const image = card.querySelector('.smo-product-card__image');
        if (image && swatch.dataset.smoImage) {
          image.src = swatch.dataset.smoImage;
          image.removeAttribute('srcset');
          image.removeAttribute('sizes');
          image.alt = image.alt.replace(/ — .+$/, '') + ' — ' + swatch.getAttribute('aria-label');
        }
        const urlLinks = card.querySelectorAll('[data-smo-card-link], [data-smo-card-title-link]');
        urlLinks.forEach(link => {
          const url = new URL(link.href, window.location.href);
          url.searchParams.set('variant', swatch.dataset.smoCardVariant);
          link.href = url.toString();
        });
        const price = card.querySelector('[data-smo-card-price]');
        if (price) price.textContent = money(swatch.dataset.smoPrice, card.dataset.currency);
      });
    });
  }

  /* Native scroll-snap gallery: the finger moves real slides, not a swapped image. */
  /**
   * Truly seamless native-swipe product carousel.
   * The previous implementation used scroll-snap but had no slide beyond the ends.
   * Physical order is [clone(last), first, ..., last, clone(first)] per shade.
   * On scroll settle at either clone, jump without animation to its identical real slide.
   */
  function initGallery(gallery) {
    const track = gallery?.querySelector('[data-smo-slide-track]');
    const thumbs = [...(gallery?.querySelectorAll('[data-smo-gallery-thumb]') || [])];
    const originals = [...(track?.querySelectorAll('[data-smo-slide]') || [])];
    if (!gallery || !track || !originals.length || originals.length !== thumbs.length) {
      return { showImage: () => {}, filterColor: () => {} };
    }
    // Visual Consistency v1: keep photo stages and thumbnails in the same
    // meaningful order, scoped to the product gallery only. Physical first,
    // real-life wear second, styled bedding third, editorial model last.
    const thumbStrip = gallery.querySelector('.smo-pdp__thumbs');
    if (thumbStrip) {
      const aligned = originals.map((slide, index) => ({
        slide, thumb: thumbs[index], originalOrder: index,
        rank: Number(slide.dataset.smoGalleryRank || 50)
      }));
      aligned.sort((a, b) => a.rank - b.rank || a.originalOrder - b.originalOrder);
      aligned.forEach(item => {
        track.appendChild(item.slide);
        thumbStrip.appendChild(item.thumb);
      });
      originals.splice(0, originals.length, ...aligned.map(item => item.slide));
      thumbs.splice(0, thumbs.length, ...aligned.map(item => item.thumb));
    }
    originals.forEach((slide, index) => { slide.dataset.smoOriginalIndex = String(index); });
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const visibleThumbs = () => thumbs.filter(thumb => !thumb.hidden);
    const activeThumb = () => thumbs.find(thumb => thumb.classList.contains('is-selected') && !thumb.hidden);
    const physicalSlides = () => [...track.querySelectorAll('[data-smo-slide]')];
    const realSlide = thumb => originals[thumbs.indexOf(thumb)];
    const originalIndexOf = slide => Number(slide?.dataset.smoOriginalIndex);
    const logicalThumbOf = slide => thumbs[originalIndexOf(slide)];
    // Align to the true left edge of each physical slide. Measuring a
    // scroll-moving bounding rect could leave a strip of a neighboring slide.
    const pos = slide => Math.max(0, slide.offsetLeft - track.clientLeft);
    let firstClone = null;
    let lastClone = null;
    let frame = 0;
    let settleTimer = 0;
    let restoreToken = 0;
    let isRebuilding = false;
    let rebuildGeneration = 0;
    let pointerDown = false;
    let userHasInteracted = false;
    let lastIndex = -1;
    const notify = thumb => {
      const index = thumbs.indexOf(thumb);
      if (index < 0) return;
      thumbs.forEach(button => {
        const chosen = button === thumb;
        button.classList.toggle('is-selected', chosen);
        button.setAttribute('aria-pressed', String(chosen));
      });
      if (index !== lastIndex) {
        lastIndex = index;
        gallery.dispatchEvent(new CustomEvent('smo:gallery-change', { detail: { index } }));
      }
    };
    const closestPhysical = () => {
      const physical = physicalSlides();
      if (!physical.length) return null;
      const region = track.getBoundingClientRect();
      const center = (region.left + region.right) / 2;
      let best = physical[0];
      let distance = Infinity;
      physical.forEach(slide => {
        const rect = slide.getBoundingClientRect();
        const delta = Math.abs((rect.left + rect.right) / 2 - center);
        if (delta < distance) { distance = delta; best = slide; }
      });
      return best;
    };
    const preloadNearby = centerSlide => {
      const physical = physicalSlides();
      const index = physical.indexOf(centerSlide);
      for (const n of [-2, -1, 0, 1, 2]) {
        const image = physical[index + n]?.querySelector('img.smo-pdp__hero-image');
        if (!image) continue;
        if (image.loading === 'lazy') image.loading = 'eager';
        if (typeof image.decode === 'function' && !image.dataset.smoDecodeRequested) {
          image.dataset.smoDecodeRequested = '1';
          image.decode().catch(() => {});
        }
      }
    };
    const alignInstantly = (slide, restoreSnap = true) => {
      if (!slide) return;
      const token = ++restoreToken;
      track.style.scrollSnapType = 'none';
      track.scrollTo({ left: pos(slide), behavior: 'auto' });
      if (restoreSnap) requestAnimationFrame(() => {
        if (token === restoreToken) track.style.scrollSnapType = '';
      });
    };
    const showThumbInStrip = thumb => {
      const strip = gallery.querySelector('.smo-pdp__thumbs');
      if (!strip || !thumb) return;
      const b = thumb.getBoundingClientRect();
      const s = strip.getBoundingClientRect();
      if (b.left < s.left + 3 || b.right > s.right - 3) {
        strip.scrollBy({
          left: (b.left + b.right - s.left - s.right) / 2,
          behavior: reducedMotion ? 'auto' : 'smooth'
        });
      }
    };
    const normalizeEdge = () => {
      if (isRebuilding || pointerDown) return;
      const physical = closestPhysical();
      if (!physical) return;
      const logical = logicalThumbOf(physical);
      if (logical) notify(logical);
      if (physical === firstClone || physical === lastClone) {
        // The clone and original are pixel-identical; this move is invisible.
        const original = originals[originalIndexOf(physical)];
        alignInstantly(original);
        preloadNearby(original);
      } else if (Math.abs(track.scrollLeft - pos(physical)) > 1) {
        // Correct incomplete snaps after trackpad scrolling, layout changes,
        // or a colour change. Never leave adjacent-photo edges visible.
        alignInstantly(physical);
      }
    };
    const requestSettle = () => {
      window.clearTimeout(settleTimer);
      settleTimer = window.setTimeout(normalizeEdge, 170);
    };
    const makeClone = (slide, edge) => {
      const cloned = slide.cloneNode(true);
      cloned.dataset.smoClone = edge;
      cloned.setAttribute('aria-hidden', 'true');
      cloned.querySelectorAll('button').forEach(button => {
        button.setAttribute('tabindex', '-1');
        button.setAttribute('aria-hidden', 'true');
      });
      cloned.querySelectorAll('img').forEach(image => { image.loading = 'eager'; });
      return cloned;
    };
    const rebuild = (colorName) => {
      const normalize = str => String(str || '').toLowerCase().trim()
        .replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      const wanted = normalize(colorName);
      const previous = activeThumb();
      isRebuilding = true;
      window.clearTimeout(settleTimer);
      ++restoreToken;
      track.style.scrollSnapType = 'none';
      thumbs.forEach((thumb, index) => {
        const color = normalize(thumb.dataset.smoMediaColor);
        const hide = Boolean(color && wanted && color !== wanted);
        thumb.hidden = hide;
        originals[index].hidden = hide;
      });
      const available = visibleThumbs();
      if (!available.length) {
        track.replaceChildren();
        firstClone = lastClone = null;
        isRebuilding = false;
        track.style.scrollSnapType = '';
        return;
      }
      const visibleOriginals = available.map(realSlide);
      // Hide/unmount incompatible colour slides, not just their thumbnails.
      if (visibleOriginals.length > 1) {
        firstClone = makeClone(visibleOriginals[visibleOriginals.length - 1], 'leading');
        lastClone = makeClone(visibleOriginals[0], 'trailing');
        track.replaceChildren(firstClone, ...visibleOriginals, lastClone);
      } else {
        firstClone = lastClone = null;
        track.replaceChildren(visibleOriginals[0]);
      }
      const selected = previous && !previous.hidden ? previous : available[0];
      notify(selected);
      const selectedSlide = realSlide(selected);
      track.scrollTo({ left: pos(selectedSlide), behavior: 'auto' });
      preloadNearby(selectedSlide);
      showThumbInStrip(selected);
      const token = ++restoreToken;
      const generation = ++rebuildGeneration;
      requestAnimationFrame(() => {
        if (generation !== rebuildGeneration) return;
        // A featured-image jump can update restoreToken after a colour rebuild.
        // In that case the jump restores snapping, but rebuilding must still end.
        if (token === restoreToken) {
          track.scrollTo({ left: pos(realSlide(activeThumb() || available[0])), behavior: 'auto' });
          track.style.scrollSnapType = '';
        }
        isRebuilding = false;
      });
    };
    const goToThumb = (thumb, instant = false) => {
      if (!thumb || thumb.hidden) return;
      // Complete any previous boundary crossing before the next button press.
      normalizeEdge();
      const visible = visibleThumbs();
      const from = visible.indexOf(activeThumb());
      const to = visible.indexOf(thumb);
      const count = visible.length;
      let physical = realSlide(thumb);
      const forwardWrap = count > 1 && from === count - 1 && to === 0;
      const backWrap = count > 1 && from === 0 && to === count - 1;
      if (forwardWrap && lastClone) physical = lastClone;
      if (backWrap && firstClone) physical = firstClone;
      const adjacent = from >= 0 && to >= 0 &&
        (Math.abs(to - from) === 1 || forwardWrap || backWrap);
      const animate = !instant && !reducedMotion && adjacent && !isRebuilding;
      notify(thumb);
      preloadNearby(physical);
      if (animate) {
        track.scrollTo({ left: pos(physical), behavior: 'smooth' });
        requestSettle();
      } else {
        alignInstantly(physical);
        requestSettle();
      }
      showThumbInStrip(thumb);
    };
    const showImage = (src, _alt, options = {}) => {
      if (!src) return;
      const pathname = value => {
        try { return new URL(value, window.location.href).pathname; }
        catch (_err) { return value; }
      };
      const exact = thumbs.find(thumb => !thumb.hidden && thumb.dataset.image === src);
      const matched = exact || thumbs.find(thumb => !thumb.hidden && pathname(thumb.dataset.image) === pathname(src));
      if (matched) goToThumb(matched, Boolean(options.instant) || !userHasInteracted);
    };
    const filterColor = name => rebuild(name);
    const updateFromScroll = () => {
      frame = 0;
      if (isRebuilding) return;
      const physical = closestPhysical();
      if (!physical) return;
      const thumb = logicalThumbOf(physical);
      if (thumb && !thumb.hidden) notify(thumb);
      preloadNearby(physical);
    };
    track.addEventListener('scroll', () => {
      if (isRebuilding) return;
      if (!frame) frame = requestAnimationFrame(updateFromScroll);
      requestSettle();
    }, { passive: true });
    // Modern Safari/Chromium settle immediately when momentum finally ends.
    track.addEventListener('scrollend', normalizeEdge);
    thumbs.forEach(thumb => {
      thumb.addEventListener('click', () => {
        userHasInteracted = true;
        goToThumb(thumb);
      });
      thumb.addEventListener('keydown', event => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        const visible = visibleThumbs();
        const at = visible.indexOf(thumb);
        const next = visible[(at + (event.key === 'ArrowRight' ? 1 : -1) + visible.length) % visible.length];
        if (!next) return;
        event.preventDefault();
        next.focus();
        next.click();
      });
    });
    track.addEventListener('keydown', event => {
      if (event.target !== track || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      const visible = visibleThumbs();
      if (!visible.length) return;
      event.preventDefault();
      const at = Math.max(0, visible.indexOf(activeThumb()));
      visible[(at + (event.key === 'ArrowRight' ? 1 : -1) + visible.length) % visible.length]?.click();
    });
    // Swipe detection keeps touch gestures from opening the image dialog.
    let down = null;
    let suppressZoomUntil = 0;
    track.addEventListener('pointerdown', event => {
      pointerDown = true;
      down = { x: event.clientX, y: event.clientY };
    }, { passive: true });
    track.addEventListener('pointermove', event => {
      if (!down) return;
      const dx = event.clientX - down.x;
      const dy = event.clientY - down.y;
      if (Math.abs(dx) > 9 && Math.abs(dx) > Math.abs(dy)) {
        suppressZoomUntil = Date.now() + 450;
      }
    }, { passive: true });
    const release = () => {
      userHasInteracted = true;
      pointerDown = false;
      down = null;
      requestSettle();
    };
    track.addEventListener('pointerup', release, { passive: true });
    track.addEventListener('pointercancel', release, { passive: true });
    const dialog = gallery.querySelector('[data-smo-zoom-dialog]');
    const zoomImage = gallery.querySelector('[data-smo-zoom-image]');
    gallery.addEventListener('click', event => {
      const trigger = event.target.closest('[data-smo-zoom-open]');
      if (!trigger || !gallery.contains(trigger) || !dialog || !zoomImage || !dialog.showModal) return;
      if (Date.now() < suppressZoomUntil) { event.preventDefault(); return; }
      const image = trigger.querySelector('img.smo-pdp__hero-image');
      if (!image) return;
      zoomImage.src = image.currentSrc || image.src;
      zoomImage.alt = image.alt;
      dialog.showModal();
    });
    gallery.querySelector('[data-smo-zoom-close]')?.addEventListener('click', () => dialog?.close());
    dialog?.addEventListener('click', event => {
      if (event.target === dialog) dialog.close();
    });
    rebuild('');
    // Responsive desktop gallery widths can change as the page settles.
    // Keep the selected image flush with the stage after such resizes.
    if (typeof ResizeObserver !== 'undefined') {
      new ResizeObserver(() => {
        if (isRebuilding || pointerDown) return;
        const thumb = activeThumb();
        if (!thumb) return;
        const slide = realSlide(thumb);
        if (slide && Math.abs(track.scrollLeft - pos(slide)) > 1) {
          alignInstantly(slide);
        }
      }).observe(track);
    }
    return { showImage, filterColor };
  }

  function initProduct(root = document) {
    root.querySelectorAll('[data-smo-product]:not([data-smo-init])').forEach(pdp => {
      pdp.dataset.smoInit = '1';
      const json = pdp.querySelector('[data-smo-variants]');
      if (!json) return;
      let variants;
      try { variants = JSON.parse(json.textContent); } catch (error) { console.error('Smoozely variants data', error); return; }
      const groups = [...pdp.querySelectorAll('[data-smo-option-position]')];
      const gallery = initGallery(pdp.querySelector('[data-smo-gallery]'));
      const priceNode = pdp.querySelector('[data-smo-product-price]');
      const cartId = pdp.querySelector('[data-smo-variant-id]');
      const add = pdp.querySelector('[data-smo-add]');
      const status = pdp.querySelector('[data-smo-status]');
      if (!cartId || !add) return;
      const chosen = groups.map(group => {
        const selected = group.querySelector('[data-smo-option-value].is-selected');
        return selected?.dataset.smoOptionValue ?? group.querySelector('[data-smo-option-select]')?.value ?? '';
      });
      const render = (fromSelection = false) => {
        const found = variants.find(variant => variant.options.length === chosen.length && variant.options.every((option, index) => option === chosen[index]));
        if (!found) {
          cartId.value = '';
          add.disabled = true;
          add.textContent = 'UNAVAILABLE COMBINATION';
          if (status) status.textContent = 'Please select another combination.';
          return;
        }
        cartId.value = found.id;
        add.disabled = !found.available;
        add.textContent = found.available ? 'ADD TO BAG' : 'SOLD OUT';
        if (priceNode) priceNode.textContent = money(found.price, pdp.dataset.currency);
        if (status) status.textContent = found.available ? '' : 'This variation is currently unavailable.';
        const colorGroup = groups.find(group => group.querySelector('[data-smo-option-value]'));
        if (colorGroup && gallery.filterColor) {
          const colorIndex = groups.indexOf(colorGroup);
          gallery.filterColor(found.options[colorIndex]);
        }
        // Colour changes replace the visible group: jump straight to the correct hero photo.
        if (found.image) gallery.showImage(found.image, found.alt, { instant: true });
        if (fromSelection) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', found.id);
          window.history.replaceState({}, '', url.toString());
        }
      };
      groups.forEach((group, index) => {
        group.querySelectorAll('[data-smo-option-value]').forEach(button => {
          button.addEventListener('click', () => {
            chosen[index] = button.dataset.smoOptionValue;
            group.querySelectorAll('[data-smo-option-value]').forEach(el => {
              const active = el === button;
              el.classList.toggle('is-selected', active);
              el.setAttribute('aria-pressed', String(active));
            });
            const label = group.querySelector('[data-smo-option-label]');
            if (label) label.textContent = group.dataset.smoShortShade === 'true' ? chosen[index].replace(/\s+22\s*M\s*Glossy\s*Silk$/i, '') : chosen[index];
            render(true);
          });
        });
        group.querySelector('[data-smo-option-select]')?.addEventListener('change', event => {
          chosen[index] = event.target.value;
          render(true);
        });
      });
      render(false);
    });
  }

  function initNavigation(root = document) {
    root.querySelectorAll('[data-smo-mobile-menu]:not([data-smo-menu-ready])').forEach(menu => {
      menu.dataset.smoMenuReady = '1';
      const summary = menu.querySelector('summary');
      if (!summary) return;
      const sync = () => {
        summary.setAttribute('aria-expanded', String(menu.open));
        summary.setAttribute('aria-label', menu.open ? 'Close navigation menu' : 'Open navigation menu');
      };
      menu.addEventListener('toggle', sync);
      menu.addEventListener('keydown', event => {
        if (event.key === 'Escape' && menu.open) {
          event.preventDefault();
          menu.open = false;
          summary.focus();
        }
      });
      document.addEventListener('pointerdown', event => {
        if (menu.open && !menu.contains(event.target)) menu.open = false;
      }, { passive: true });
      window.addEventListener('resize', () => {
        if (window.innerWidth > 1050 && menu.open) menu.open = false;
      }, { passive: true });
      sync();
    });
  }

  function init(root = document) {
    initNavigation(root);
    initReveals(root);
    initPalettes(root);
    initCards(root);
    initProduct(root);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', event => init(event.target));
})();
